// RFC 6238 time-based one-time codes, the kind any authenticator app shows:
// HMAC-SHA1 over the 30 second step, six digits.

const PERIOD = 30;
const DIGITS = 6;
const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

const toBase32 = (bytes: Uint8Array) => {
  const bits = Array.from(bytes, b => b.toString(2).padStart(8, '0')).join('');
  return Array.from(
    { length: Math.ceil(bits.length / 5) },
    (_, i) => BASE32[parseInt(bits.slice(i * 5, i * 5 + 5).padEnd(5, '0'), 2)],
  ).join('');
};

const fromBase32 = (text: string) => {
  const bits = Array.from(
    text.toUpperCase().replace(/[\s=]/g, ''),
    c => {
      const i = BASE32.indexOf(c);
      if (i < 0) throw new Error(`not base32: ${c}`);
      return i.toString(2).padStart(5, '0');
    },
  ).join('');
  return Uint8Array.from(
    { length: Math.floor(bits.length / 8) },
    (_, i) => parseInt(bits.slice(i * 8, i * 8 + 8), 2),
  );
};

export const newTotpSecret = () =>
  toBase32(crypto.getRandomValues(new Uint8Array(20)));

// what the authenticator app scans
export const totpUri = (secret: string, account: string) =>
  `otpauth://totp/${
    encodeURIComponent(`Notes:${account}`)
  }?${new URLSearchParams(
    { secret, issuer: 'Notes', period: String(PERIOD), digits: String(DIGITS) },
  )}`;

export const totpCode = async (
  secret: string,
  time = Date.now(),
  digits = DIGITS,
) => {
  const counter = new DataView(new ArrayBuffer(8));
  counter.setBigUint64(0, BigInt(Math.floor(time / 1000 / PERIOD)));
  const key = await crypto.subtle.importKey(
    'raw',
    fromBase32(secret),
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  );
  const mac = new Uint8Array(
    await crypto.subtle.sign('HMAC', key, counter.buffer),
  );
  const at = mac[mac.length - 1] & 0xf;
  const value = new DataView(mac.buffer).getUint32(at) & 0x7fffffff;
  return String(value % 10 ** digits).padStart(digits, '0');
};

// one step either side of now, for a clock that drifts or a slow typist
export const verifyTotp = async (
  secret: string,
  code: string,
  time = Date.now(),
) => {
  const typed = code.replace(/\s/g, '');
  const codes = await Promise.all(
    [-1, 0, 1].map(step => totpCode(secret, time + step * PERIOD * 1000)),
  );
  return codes.includes(typed);
};
